import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'
import 'remixicon/fonts/remixicon.css'

export default function Home() {
  const coreServices = [
    {
      icon: 'ri-smartphone-line',
      title: 'Online Food Ordering',
      description: 'A seamless digital platform allowing customers to browse menus, place orders, and make secure payments.',
      color: 'from-orange-400 to-orange-600'
    },
    {
      icon: 'ri-truck-line',
      title: 'Reliable Delivery Network',
      description: 'Professional delivery operations ensuring timely and safe transportation of meals.',
      color: 'from-green-400 to-green-600'
    },
    {
      icon: 'ri-store-2-line',
      title: 'Vendor Partnerships',
      description: 'We partner with reputable restaurants, food outlets, and home-based kitchens to expand their market reach.',
      color: 'from-purple-400 to-purple-600'
    },
    {
      icon: 'ri-building-2-line',
      title: 'Corporate & Bulk Orders',
      description: 'Structured solutions for corporate events, staff meals, conferences, and bulk food requests.',
      color: 'from-blue-400 to-blue-600'
    }
  ]

  const whyAmiBites = [
    { icon: 'ri-checkbox-circle-line', text: 'Structured and professionally managed operations' },
    { icon: 'ri-shield-check-line', text: 'Secure and reliable payment systems' },
    { icon: 'ri-flashlight-line', text: 'Fast and efficient order processing' },
    { icon: 'ri-headphone-line', text: 'Dedicated customer support' },
    { icon: 'ri-award-line', text: 'Commitment to food quality and vendor standards' }
  ]

  const stats = [
    { label: 'Happy Customers', value: '500+', icon: 'ri-group-line' },
    { label: 'Partner Restaurants', value: '10+', icon: 'ri-restaurant-2-line' },
    { label: 'Deliveries Daily', value: '500+', icon: 'ri-truck-line' },
    { label: 'Average Rating', value: '4.8', icon: 'ri-star-line' }
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  }

  return (
    <div className="overflow-hidden">
      <SEO 
        title="Ami Bites - Zimbabwe's #1 Food Delivery & Online Ordering Platform"
        description="Order quality meals from trusted restaurants in Zimbabwe. Fast, reliable food delivery service with 500+ happy customers. Corporate catering & bulk orders available. Part of Pitch For Me Group."
        keywords="food delivery Zimbabwe, online food ordering Zimbabwe, restaurant delivery Harare, food delivery app, Ami Bites, corporate catering Zimbabwe, bulk food orders, Zimbabwe restaurants, food delivery service, online ordering platform, Pitch For More Group"
        canonical="https://amibites.com/"
      />
      <section className="relative min-h-screen flex items-center justify-center pt-20 pb-16 px-4">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-white to-green-50"></div>
        
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            animate={{
              y: [0, -20, 0],
              rotate: [0, 5, 0]
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="absolute top-20 left-10 w-20 h-20 bg-orange-200 rounded-full opacity-30 blur-xl"
          />
          <motion.div
            animate={{
              y: [0, 20, 0],
              rotate: [0, -5, 0]
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="absolute bottom-20 right-10 w-32 h-32 bg-green-200 rounded-full opacity-30 blur-xl"
          />
        </div>

        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 mb-4 px-4 py-2 bg-orange-100 text-orange-600 rounded-full text-sm font-semibold"
            >
              <i className="ri-trophy-line text-base"></i> Zimbabwe's #1 Food Delivery Platform
            </motion.div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="text-gradient">Ami Bites</span>
            </h1>
            
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-6 leading-tight">
              <span className="text-gradient-green">Premium Online Food Ordering & Delivery Platform</span>
            </h2>
            
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Connecting customers to quality restaurants and food vendors through a secure, reliable, and technology-driven delivery network.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/order">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition-shadow flex items-center justify-center space-x-2"
                >
                  <span>Order Now</span>
                  <i className="ri-flashlight-line text-xl"></i>
                </motion.button>
              </Link>
              
              <Link to="/partner-with-us">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-full sm:w-auto px-8 py-4 bg-white border-2 border-green-600 text-green-600 rounded-xl font-semibold text-lg hover:bg-green-50 transition-colors flex items-center justify-center space-x-2"
                >
                  <span>Partner With Us</span>
                  <i className="ri-hand-heart-line text-xl"></i>
                </motion.button>
              </Link>
            </div>

            <div className="mt-12 flex items-center space-x-8">
              <div className="flex -space-x-3">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 border-4 border-white flex items-center justify-center text-white font-bold">
                    {i === 4 ? '😋' : '👨‍🍳'}
                  </div>
                ))}
              </div>
              <div>
                <div className="flex items-center space-x-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <i key={i} className="ri-star-fill text-xl text-orange-400"></i>
                  ))}
                </div>
                <p className="text-sm text-gray-600 mt-1">Loved by 500+ customers</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative z-10">
              <motion.img
                src="/transparent-logo.png"
                alt="Ami Bites Delivery"
                className="w-full h-auto animate-float"
              />
            </div>
            
            <motion.div
              animate={{
                scale: [1, 1.1, 1],
                rotate: [0, 5, 0]
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="absolute -bottom-10 -right-10 w-64 h-64 bg-gradient-to-br from-orange-300 to-orange-500 rounded-full opacity-20 blur-3xl"
            />
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                rotate: [0, -5, 0]
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="absolute -top-10 -left-10 w-64 h-64 bg-gradient-to-br from-green-300 to-green-500 rounded-full opacity-20 blur-3xl"
            />
          </motion.div>
        </div>
      </section>

      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="text-center"
              >
                <motion.div
                  initial={{ rotate: -180, scale: 0 }}
                  whileInView={{ rotate: 0, scale: 1 }}
                  whileHover={{ rotate: 360, scale: 1.1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, type: 'spring', stiffness: 200 }}
                  className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-100 to-green-100 rounded-2xl mb-4"
                >
                  <i className={`${stat.icon} text-3xl text-orange-600`}></i>
                </motion.div>
                <div className="text-3xl md:text-4xl font-bold text-gradient mb-2">{stat.value}</div>
                <div className="text-gray-600 text-sm md:text-base">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              About <span className="text-gradient">Ami Bites</span>
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto bg-gradient-to-br from-orange-50 to-green-50 rounded-3xl p-8 md:p-12 mb-8"
          >
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-6">
              Ami Bites is a professionally managed online food ordering and delivery platform committed to delivering convenience, efficiency, and quality service.
            </p>
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed">
              As a member of the <strong className="text-gradient">Pitch For Me Group of Companies</strong>, we operate with strong corporate governance, structured systems, and a commitment to excellence across all our services.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 px-4 bg-gradient-to-br from-orange-50 via-white to-green-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Our <span className="text-gradient">Core Services</span>
            </h2>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 gap-8"
          >
            {coreServices.map((service, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -10, transition: { duration: 0.2 } }}
                className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity duration-300`}></div>
                
                <div className="flex items-start space-x-4">
                  <motion.div
                    initial={{ rotate: -180, scale: 0 }}
                    whileInView={{ rotate: 0, scale: 1 }}
                    whileHover={{ rotate: 360, scale: 1.1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, type: 'spring', stiffness: 200 }}
                    className={`flex-shrink-0 inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${service.color} rounded-2xl`}
                  >
                    <i className={`${service.icon} text-3xl text-white`}></i>
                  </motion.div>
                  
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-3 text-gray-900">{service.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{service.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Why <span className="text-gradient">Ami Bites</span>?
            </h2>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="max-w-4xl mx-auto space-y-4"
          >
            {whyAmiBites.map((item, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ x: 10, transition: { duration: 0.2 } }}
                className="flex items-center space-x-4 p-6 bg-gradient-to-r from-orange-50 to-green-50 rounded-2xl shadow-md hover:shadow-lg transition-all"
              >
                <motion.div
                  initial={{ rotate: -180, scale: 0 }}
                  whileInView={{ rotate: 0, scale: 1 }}
                  whileHover={{ rotate: 360, scale: 1.1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, type: 'spring', stiffness: 200 }}
                  className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center"
                >
                  <i className={`${item.icon} text-2xl text-white`}></i>
                </motion.div>
                <p className="text-lg font-medium text-gray-800">{item.text}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-20 px-4 bg-gradient-to-r from-orange-600 to-green-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/2 translate-y-1/2"></div>
        </div>

        <div className="relative max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-4xl md:text-5xl font-bold text-white mb-6"
            >
              Ready to Order?
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-xl text-white/90 mb-8 max-w-2xl mx-auto"
            >
              Download our app or order online now. Your favorite food is just a tap away!
            </motion.p>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <Link to="/order">
                <motion.button
                  whileHover={{ scale: 1.05, y: -5 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 bg-white text-orange-600 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition-shadow flex items-center space-x-2"
                >
                  <i className="ri-restaurant-line text-xl"></i>
                  <span>Start Ordering</span>
                </motion.button>
              </Link>
              
              <motion.a
                href="https://play.google.com/store/apps/details?id=amibites.com.myapp&pcampaignid=web_share"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-xl font-semibold text-lg hover:bg-white/10 transition-colors flex items-center space-x-2"
              >
                <span>Download App</span>
                <i className="ri-smartphone-line text-xl"></i>
              </motion.a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}
